/**
 * TransactionEmptyState - Rich Empty State Component
 * 
 * Beautiful empty state with illustrations and helpful CTAs
 */

import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { 
  Receipt, 
  Plus, 
  TrendingUp, 
  TrendingDown, 
  ArrowLeftRight,
  Sparkles
} from 'lucide-react';
import { motion } from 'motion/react';

interface TransactionEmptyStateProps {
  onCreateTransaction: () => void;
  hasFilters?: boolean;
  onClearFilters?: () => void;
}

export function TransactionEmptyState({
  onCreateTransaction,
  hasFilters = false,
  onClearFilters,
}: TransactionEmptyStateProps) {
  if (hasFilters) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-16"
      >
        <div className="relative inline-block mb-6">
          <Receipt className="w-24 h-24 text-muted-foreground/30" />
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className="absolute -top-2 -right-2 bg-primary rounded-full p-2"
          >
            <Sparkles className="w-5 h-5 text-primary-foreground" />
          </motion.div>
        </div>

        <h3 className="text-xl font-semibold mb-2">
          Nenhum resultado encontrado
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto mb-6">
          Não encontramos lançamentos com os filtros aplicados. Tente ajustar os critérios de busca.
        </p>

        <div className="flex gap-3 justify-center">
          <Button onClick={onClearFilters} variant="outline" size="lg">
            Limpar Filtros
          </Button>
          <Button onClick={onCreateTransaction} size="lg">
            <Plus className="w-5 h-5 mr-2" />
            Novo Lançamento
          </Button>
        </div>
      </motion.div>
    );
  }

  const quickStartActions = [
    {
      icon: TrendingUp,
      label: 'Receita',
      description: 'Vendas, pagamentos recebidos',
      color: 'text-green-600 bg-green-50 hover:bg-green-100 border-green-200',
    },
    {
      icon: TrendingDown,
      label: 'Despesa',
      description: 'Compras, pagamentos realizados',
      color: 'text-red-600 bg-red-50 hover:bg-red-100 border-red-200',
    },
    {
      icon: ArrowLeftRight,
      label: 'Transferência',
      description: 'Movimentação entre contas',
      color: 'text-blue-600 bg-blue-50 hover:bg-blue-100 border-blue-200',
    },
  ];

  return (
    <div className="space-y-8 py-12">
      {/* Main Empty State */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ 
            type: "spring",
            stiffness: 260,
            damping: 20,
            delay: 0.1
          }}
          className="relative inline-block mb-6"
        >
          <div className="w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center">
            <Receipt className="w-16 h-16 text-primary" />
          </div>
          
          {/* Floating icons animation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="absolute -top-2 -right-2"
          >
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="absolute -bottom-2 -left-2"
          >
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
          </motion.div>
        </motion.div>

        <h3 className="text-2xl font-bold mb-3">
          Comece a registrar seus lançamentos
        </h3>
        <p className="text-muted-foreground max-w-lg mx-auto text-lg mb-8">
          Mantenha suas finanças organizadas. Registre receitas, despesas e transferências em poucos cliques.
        </p>

        <Button onClick={onCreateTransaction} size="lg" className="h-12 px-8 shadow-lg">
          <Plus className="w-5 h-5 mr-2" />
          Criar Primeiro Lançamento
        </Button>
      </motion.div>

      {/* Quick Start Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h4 className="text-sm font-semibold text-center mb-4 text-muted-foreground">
          O que você pode registrar
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {quickStartActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <motion.div
                key={action.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <Card 
                  variant="interactive"
                  className={`p-5 border-2 ${action.color}`}
                  onClick={onCreateTransaction}
                >
                  <div className="text-center space-y-2">
                    <div className="w-12 h-12 rounded-full bg-white/80 mx-auto flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6" />
                    </div>
                    <p className="font-semibold">{action.label}</p>
                    <p className="text-xs opacity-80">{action.description}</p>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Tips */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="max-w-2xl mx-auto"
      >
        <Card className="p-6 bg-muted/30">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Dicas para começar
          </h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>Use categorias consistentes para facilitar relatórios futuros</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>Configure lançamentos recorrentes para contas fixas mensais</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>Registre todas as transações, mesmo as pequenas, para ter controle total</span>
            </li>
          </ul>
        </Card>
      </motion.div>
    </div>
  );
}
